/* =========================================================
 *  YouTube API loader — must run before either element
 * ========================================================= */
const ytApiReady = (() => {
  let resolve;
  const promise = new Promise(r => { resolve = r; });

  if (window.YT && window.YT.Player) { resolve(); return promise; }

  const prev = window.onYouTubeIframeAPIReady;
  window.onYouTubeIframeAPIReady = () => {
    if (typeof prev === 'function') prev();
    resolve();
  };

  if (!document.querySelector('script[src*="youtube.com/iframe_api"]')) {
    const s = document.createElement('script');
    s.src = 'https://www.youtube.com/iframe_api';
    s.async = true;
    document.head.appendChild(s);
  }

  return promise;
})();


/* =========================================================
 *  <bd-yt-video> — the player element
 * ========================================================= */
class BdYtVideo extends HTMLElement {
  static get observedAttributes() {
    return ['data-id'];
  }

  constructor() {
    super();
    this.attachShadow({ mode: 'open' });
    this._player = null;
    this._ready = false;
    this._target = null;
    this._currentId = null;
    this._initPromise = null;
  }

  connectedCallback() {
    this._render();
    const id = this.getAttribute('data-id');
    if (id) this._initPlayer(id);
  }

  disconnectedCallback() {
    this._destroyPlayer();
  }

  attributeChangedCallback(name, oldVal, newVal) {
    if (name === 'data-id' && oldVal !== newVal) {
      if (newVal) {
        this._loadVideo(newVal);
      } else {
        this._destroyPlayer();
      }
    }
  }

  /* --- Internal --- */

  _render() {
    this.shadowRoot.innerHTML = `
      <style>
        :host { display: block; width: 100%; }
        .wrap {
           
          width: 100%;
           height: 100%;
        }
        
        iframe {
          width: 100%;
          height: 100%;
        }
      </style>
      <div class="wrap"><div id="yt-target"></div></div>
    `;
    this._target = this.shadowRoot.getElementById('yt-target');
  }

  async _initPlayer(videoId) {
    /* Wait for the API script to be ready */
    await ytApiReady;
    this._createPlayer(videoId);
  }

  _createPlayer(videoId) {
    if (this._player) this._destroyPlayer();

    /* If destroy removed the target div, re-render */
    if (!this._target || !this._target.isConnected) this._render();

    this._ready = false;
    this._currentId = videoId;
    this._initPromise = new Promise((resolve) => {
      this._initResolve = resolve;
    });

    this._player = new YT.Player(this._target, {
      videoId,
      width: '100%',
      height: '100%',
      playerVars: {
        autoplay: 0,
        controls: 1,
        rel: 0,
        modestbranding: 1,
        playsinline: 1,
        iv_load_policy: 3,
      },
      events: {
        onReady: (e) => {
          this._ready = true;
          if (this._initResolve) {
            this._initResolve();
            this._initResolve = null;
          }
          this.dispatchEvent(new CustomEvent('bd:ready', {
            detail: { target: e.target },
            bubbles: true,
          }));
        },
        onStateChange: (e) => {
          const state = e.data;
          const names = {
            '-1': 'unstarted', '0': 'ended', '1': 'playing',
            '2': 'paused', '3': 'buffering', '5': 'cued'
          };
          this.dispatchEvent(new CustomEvent('bd:statechange', {
            detail: { state, stateName: names[String(state)] || 'unknown' },
            bubbles: true,
          }));
          if (state === YT.PlayerState.PLAYING)
            this.dispatchEvent(new CustomEvent('bd:play', { bubbles: true }));
          if (state === YT.PlayerState.PAUSED)
            this.dispatchEvent(new CustomEvent('bd:pause', { bubbles: true }));
          if (state === YT.PlayerState.ENDED)
            this.dispatchEvent(new CustomEvent('bd:ended', { bubbles: true }));
        },
      },
    });
  }

  /**
   * Smart load: reuse the existing player when possible,
   * full rebuild only when necessary.
   */
  async _loadVideo(videoId) {
    this._currentId = videoId;

    /* Fast path — player exists and is ready, just swap the video */
    if (this._player && this._ready) {
      this._player.loadVideoById(videoId);
      /* loadVideoById resets ready state internally,
         fire ready again so bd-videos knows it can play */
      this.dispatchEvent(new CustomEvent('bd:ready', {
        detail: { videoId },
        bubbles: true,
      }));
      return;
    }

    /* Slow path — no player yet or not ready, build from scratch */
    await ytApiReady;
    this._createPlayer(videoId);
  }

  _destroyPlayer() {
    if (this._player) {
      try { this._player.destroy(); } catch (_) {}
      this._player = null;
      this._ready = false;
    }
    if (!this._target || !this._target.isConnected) this._render();
  }

  /**
   * Returns a promise that resolves when the player is ready.
   * Resolves immediately if already ready.
   */
  get ready() {
    if (this._ready) return Promise.resolve();
    return this._initPromise || Promise.resolve();
  }

  /* --- Public API --- */

  play() {
    if (this._player && this._ready) this._player.playVideo();
  }

  pause() {
    if (this._player && this._ready) this._player.pauseVideo();
  }

  stop() {
    if (this._player && this._ready) this._player.stopVideo();
  }

  seekTo(seconds, allowSeekAhead = true) {
    if (this._player && this._ready)
      this._player.seekTo(seconds, allowSeekAhead);
  }

  setVolume(volume) {
    if (this._player && this._ready)
      this._player.setVolume(Math.max(0, Math.min(100, volume)));
  }

  getVolume() {
    return this._player && this._ready ? this._player.getVolume() : 0;
  }

  getCurrentTime() {
    return this._player && this._ready ? this._player.getCurrentTime() : 0;
  }

  getDuration() {
    return this._player && this._ready ? this._player.getDuration() : 0;
  }

  getPlayerState() {
    return this._player && this._ready ? this._player.getPlayerState() : -1;
  }

  getBuffered() {
    return this._player && this._ready ? this._player.getVideoLoadedFraction() : 0;
  }

  loadVideoById(videoId) {
    this._loadVideo(videoId);
  }

  get isReady() {
    return this._ready;
  }

  get player() {
    return this._player;
  }
}

customElements.define('bd-yt-video', BdYtVideo);


/* =========================================================
 *  <bd-videos> — the thumbnail-to-player coordinator
 * ========================================================= */
class BdVideos extends HTMLElement {

  constructor() {
    super();
    this._player = null;
    this._onReady = null;
  }

  connectedCallback() {
    this._player = this.querySelector('bd-yt-video');

    if (this._player) {
      const initialId = this._player.getAttribute('data-id');
      if (initialId) this._setActive(initialId);
    }

    this.addEventListener('click', (e) => {
      const thumb = e.target.closest('.thumb-block');
      if (!thumb || !this._player) return;

      const videoId = thumb.getAttribute('data-video-target');
      if (!videoId) return;

      this._loadThumb(videoId);
    });
  }

  async _loadThumb(videoId) {
    const currentId = this._player.getAttribute('data-id');

    /* Same video — toggle play/pause */
    if (currentId === videoId && this._player.isReady) {
      const state = this._player.getPlayerState();
      if (state === 1) {
        this._player.pause();
      } else {
        this._player.play();
      }
      return;
    }

    /* Clean up any previous one-shot listener */
    if (this._onReady) {
      this._player.removeEventListener('bd:ready', this._onReady);
      this._onReady = null;
    }

    /* One-shot: play as soon as the new video is ready */
    this._onReady = () => {
      this._player.play();
      this._player.removeEventListener('bd:ready', this._onReady);
      this._onReady = null;
    };
    this._player.addEventListener('bd:ready', this._onReady);

    /* Tell the player to load the new video */
    this._player.setAttribute('data-id', videoId);
    this._setActive(videoId);

    this._player.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }

  _setActive(videoId) {
    this.querySelectorAll('.thumb-block').forEach((thumb) => {
      thumb.classList.toggle(
        'active',
        thumb.getAttribute('data-video-target') === videoId
      );
    });
  }

  play()  { this._player && this._player.play(); }
  pause() { this._player && this._player.pause(); }
  stop()  { this._player && this._player.stop(); }

  get videoElement() { return this._player; }
}

customElements.define('bd-videos', BdVideos);